import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const InquiryTypeSelector = ({ selectedType, onTypeChange }) => {
  const inquiryTypes = [
    {
      id: 'club',
      icon: 'Building2',
      title: 'European Club',
      subtitle: 'Scout & Technical Director',
      description: 'Access verified African talent database, request player profiles, and initiate transfer discussions.',
      color: 'primary',
      benefits: ['Verified player data', 'Video analysis', 'Transfer support', 'Due diligence']
    },
    {
      id: 'player',
      icon: 'User',
      title: 'African Player',
      subtitle: 'Elite Talent',
      description: 'Apply for representation, explore European opportunities, and access career development programs.',
      color: 'accent',
      benefits: ['Career guidance', 'European placement', 'Contract negotiation', 'Development programs']
    },
    {
      id: 'technical',
      icon: 'Clipboard',
      title: 'Technical Staff',
      subtitle: 'Coaches & Specialists',
      description: 'Submit credentials, explore coaching opportunities in Europe, and access placement services.',
      color: 'secondary',
      benefits: ['Credential verification', 'Job matching', 'Interview preparation', 'Visa support']
    },
    {
      id: 'partner',
      icon: 'Handshake',
      title: 'Industry Partner',
      subtitle: 'Strategic Collaboration',
      description: 'Explore partnership opportunities, discuss collaboration models, and access market intelligence.',
      color: 'success',
      benefits: ['Partnership models', 'Market insights', 'Co-branding', 'Revenue sharing']
    }
  ];

  const getColorClasses = (color, isSelected) => {
    const baseColors = {
      primary: isSelected ? 'bg-primary text-primary-foreground border-primary' : 'bg-primary/5 text-primary border-primary/20 hover:bg-primary/10',
      accent: isSelected ? 'bg-accent text-accent-foreground border-accent' : 'bg-accent/5 text-accent border-accent/20 hover:bg-accent/10',
      secondary: isSelected ? 'bg-secondary text-secondary-foreground border-secondary' : 'bg-secondary/5 text-secondary border-secondary/20 hover:bg-secondary/10',
      success: isSelected ? 'bg-success text-success-foreground border-success' : 'bg-success/5 text-success border-success/20 hover:bg-success/10'
    };
    return baseColors?.[color] || baseColors?.primary;
  };

  return (
    <section className="py-12 md:py-16 lg:py-20 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-8 md:mb-12">
          <div className="inline-flex items-center gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-primary/10 rounded-full mb-4">
            <Icon name="Users" size={16} className="text-primary" />
            <span className="text-xs md:text-sm font-medium text-primary">Tailored Support</span>
          </div>
          
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold font-headline text-foreground mb-3 md:mb-4">
            Select Your Inquiry Type
          </h2>
          
          <p className="text-sm md:text-base lg:text-lg text-muted-foreground max-w-3xl mx-auto">
            Choose the category that best describes your inquiry to receive personalized assistance and relevant information from our specialized team.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {inquiryTypes?.map((type) => {
            const isSelected = selectedType === type?.id;
            
            return (
              <button
                key={type?.id}
                onClick={() => onTypeChange(type?.id)}
                className={`group text-left border-2 rounded-xl p-4 md:p-6 transition-all duration-300 ${getColorClasses(type?.color, isSelected)} ${isSelected ? 'shadow-elevated scale-[1.02]' : 'hover:shadow-lg'}`}
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className={`flex-shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center transition-transform duration-300 ${isSelected ? 'bg-white/20 scale-110' : 'bg-current/10'}`}>
                    <Icon 
                      name={type?.icon} 
                      size={24} 
                      className={isSelected ? 'text-current' : ''}
                    />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg md:text-xl font-bold font-headline mb-1">
                      {type?.title}
                    </h3>
                    <p className={`text-xs md:text-sm font-medium ${isSelected ? 'opacity-90' : 'opacity-70'}`}>
                      {type?.subtitle}
                    </p>
                  </div>
                  
                  {isSelected && (
                    <div className="flex-shrink-0 w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                      <Icon name="Check" size={16} />
                    </div>
                  )}
                </div>
                <p className={`text-sm md:text-base mb-4 ${isSelected ? 'opacity-90' : 'opacity-80'}`}>
                  {type?.description}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {type?.benefits?.map((benefit, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Icon name="CheckCircle2" size={14} className={isSelected ? 'text-current' : 'opacity-70'} />
                      <span className={`text-xs md:text-sm ${isSelected ? 'opacity-90' : 'opacity-70'}`}>
                        {benefit}
                      </span>
                    </div>
                  ))}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default InquiryTypeSelector;